/**
 * SEO Utilities
 *
 * Provides canonical URL generation, robots meta directive logic,
 * and programmatic metadata for public pages.
 */

import { buildDirectoryUrl, buildStyleUrl, buildGymUrl, toDisplaySuburb, parseStyleSlug, toLocationSlug, getSuburbPostcode } from './slugs';
import { resolveSuburbPostcode } from './suburbPostcode.mjs';
import { stateSlugToDisplay, stateDisplayToSlug } from './stateCore.mjs';
import type { StateSlug } from './stateCore.mjs';
import { DEFAULT_STATE_DISPLAY, stateDirectoryRootSlug, buildStateDirectoryRootUrl } from './stateRoutes';
import { toOpeningHoursSpecification } from './openingHours';

// ============================================
// BLOG INDEX POSTURE — single source of truth
// ============================================

/**
 * BLOG_INDEX_POSTURE — whether the blog is hidden from search engines or live to them.
 *
 *   'staging'    — noindex,nofollow on all blog HTML; sitemap-blog.xml generated
 *                  but orphaned from sitemap.xml. Blog reachable by direct URL
 *                  and in-app navigation only.
 *   'production' — index,follow on all blog HTML; sitemap-blog.xml referenced
 *                  from sitemap.xml. Blog discoverable by search engines.
 *
 * Wiring: this constant is the single source of truth. It drives BLOG_ROBOTS
 * (below), and every posture-sensitive surface now reads one of the two — wired
 * as of items 40 + 2 (deployed 2026-06-08), so a flip propagates without
 * per-surface edits:
 *   - pages/BlogIndexPage.tsx, pages/BlogCategoryPage.tsx, pages/BlogPostPage.tsx
 *     — runtime robots = BLOG_ROBOTS (no hardcoded literal in any of them)
 *   - vite.config.ts — the SSG injects a <meta name="robots"> tag whose content
 *     is BLOG_ROBOTS on every /blog route, and stamps the posture into
 *     dist/blog-manifest.json
 *   - scripts/generate-sitemaps.mjs — references sitemap-blog.xml from sitemap.xml
 *     only when the manifest posture is 'production' (fail-closed to orphaned)
 *   (STATIC_PAGE_META needs no robots field: blog consumers override it with
 *    BLOG_ROBOTS, and the SSG injection above is authoritative for crawlers.)
 *
 * To flip: change BLOG_INDEX_POSTURE below — this one constant — then rebuild and
 * redeploy. scripts/verify-blog-deploy.mjs reads the posture from
 * dist/blog-manifest.json and enforces posture↔artifact agreement (B1: blog-HTML
 * robots; B3: sitemap-blog.xml referenced vs orphaned), so flipping the constant
 * without a matching rebuild fails the gate.
 */
export type BlogIndexPosture = 'staging' | 'production';
export const BLOG_INDEX_POSTURE: BlogIndexPosture = 'production';

/**
 * Robots directive derived from BLOG_INDEX_POSTURE. Single source of truth
 * for the robots value blog pages emit; consumers should read this rather
 * than hardcoding either string so a posture flip at BLOG_INDEX_POSTURE
 * propagates without per-page edits.
 *
 *   posture='staging'    → 'noindex,nofollow'
 *   posture='production' → 'index,follow' (matches the house indexable
 *                          convention used across non-blog metadata builders).
 */
// Cast widens the const's narrowed literal type for the comparison —
// without it, TS sees BLOG_INDEX_POSTURE as 'staging' and flags the
// 'production' branch as dead code. The cast lets the comparison
// remain a runtime decision so a posture flip at the declaration above
// actually changes BLOG_ROBOTS without further TS edits here.
export const BLOG_ROBOTS: 'noindex,nofollow' | 'index,follow' =
    (BLOG_INDEX_POSTURE as string) === 'production' ? 'index,follow' : 'noindex,nofollow';

// ============================================
// CONSTANTS
// ============================================

/**
 * Secondary filter query params that trigger noindex.
 * 'styles' is included because single-style filtering is now expressed
 * in the URL path (/nsw/suburb/gyms/style-slug). Any ?styles= query param
 * is therefore a non-canonical multi-filter and should be noindex.
 */
const SECONDARY_FILTER_PARAMS = new Set([
    'styles',
    'age',
    'ageGroup',
    'ageGroups',
    'exp',
    'experience',
    'experienceLevel',
    'distance',
    'radius',
    'sort',
    'sortBy',
    'rating',
    'minRating',
    'price',
    'time',
    'page',
    'view',
    'viewMode',
    'gymName'
]);

/**
 * Minimum gym count for a suburb/suburb×style page to be in the INDEXED tier.
 * Operator decision (2026-05-31): index ≥3-gym pages; noindex (still follow)
 * thinner ones so link equity flows but they don't bloat the index.
 *
 * NOTE: scripts/generate-ssg-routes.mjs has its own MIN_GYMS_PER_LOCATION /
 * MIN_GYMS_PER_STYLE = 3 (it's a standalone .mjs that can't import this .ts).
 * Keep all three in sync — this is the runtime/SSG-injection source of truth;
 * the .mjs constants govern route emission. They must agree on the value (3).
 */
export const MIN_GYMS_FOR_INDEX = 3;

/**
 * The runtime half of the indexed-tier policy, as a pure function so it can be tested.
 *
 * `getDirectoryMetadata` derives the thin-page noindex from an `opts.gymCount`, which
 * also feeds the DESCRIPTION builder ("Find N Boxing gyms in X"). Runtime callers must
 * not supply it for that reason — descriptions are deferred to their own slice and must
 * not change. This applies the indexability half of the rule to the already-built
 * metadata instead, leaving the description untouched.
 *
 * Two ways a directory route must not claim to be indexable:
 *   · THIN — prerendered, but under the gym threshold. 411 such suburb pages are served
 *     `noindex,follow` by the SSG and are deliberately kept out of Google.
 *   · NOT BUILT — no prerendered file exists, so Firebase serves /notfound.html. The
 *     client must agree rather than flip a dead route back to `index,follow`.
 *
 * @param robots      the value getDirectoryMetadata already computed
 * @param isPrerendered whether the SSG payload belongs to THIS route
 * @param prerenderedGymCount gyms in that payload (ignored unless isPrerendered)
 * @param hasSuburb   whether a suburb is in play — the not-built rule only applies then
 */
export type RobotsDirective = 'index,follow' | 'noindex,follow' | 'noindex,nofollow';

export function applyRuntimeDirectoryRobots(
    robots: RobotsDirective,
    isPrerendered: boolean,
    prerenderedGymCount: number,
    hasSuburb: boolean,
): RobotsDirective {
    if (robots !== 'index,follow') return robots;
    const isThin = isPrerendered && prerenderedGymCount < MIN_GYMS_FOR_INDEX;
    const isNotBuilt = hasSuburb && !isPrerendered;
    return (isThin || isNotBuilt) ? 'noindex,follow' : robots;
}

/**
 * The postcode to print in a suburb/style page TITLE (audit O2; owner format decided
 * 2026-07-31 — "Martial Arts Gyms in Adamstown NSW 2289").
 *
 * THE REGISTER IS AUTHORITATIVE. `registerPostcode` comes from the official locality
 * register (`locations_au/{version}/states/{STATE}/entries`) — the same source that
 * already prunes the hub A–Z and every sitemap. When present it wins outright.
 *
 * A FIRST IMPLEMENTATION DERIVED THIS FROM THE PAGE'S OWN GYMS INSTEAD, AND WAS WRONG.
 * It tallied `gym.postcode` and printed the plurality. An adversarial review caught it
 * against the real build: `/nsw/hunters-hill/gyms` — an indexable, sitemapped page —
 * was titled "Hunters Hill NSW 2111" when the register says 2110, because the suburb's
 * three records include one venue listed TWICE (identical lat/lng) whose duplicate
 * outvoted the correct postcode. Across the corpus that derivation contradicted the
 * register on 24 of 601 suburb pages. A gym's postcode is a property of that BUSINESS,
 * not of the suburb the page is about, so no vote over gyms can be authoritative —
 * and the claim that it "cannot disagree with the address the same page displays" was
 * falsified by the very build that shipped it.
 *
 * The gym tally survives only as a FALLBACK for suburbs the register does not cover,
 * where some postcode beats none. It dedupes by identity first so one venue recorded
 * twice cannot outvote a real one, takes the most common (not the first, so result
 * ordering cannot flip a title between builds), and breaks ties on the lowest value
 * purely for determinism — a tie is arbitrary by nature and is reported by the caller's
 * build-time check rather than trusted.
 *
 * Lives in lib/ rather than vite.config.ts so the BUILD and the BROWSER derive it
 * identically and it is unit-testable (the lib/ssgSeoAssembly precedent).
 *
 * THE ALGORITHM MOVED to lib/suburbPostcode.mjs (audit O6) and this is now a thin
 * delegate. The same number decides the page's TITLE and — since O6 — its URL, and the
 * URL is minted by plain-Node generator scripts that cannot import TypeScript. Keeping
 * the logic here would have meant a second copy in .mjs, i.e. a title and an address
 * that can silently disagree. One implementation, three runtimes.
 */
export function titlePostcode(
    gyms: Array<{ postcode?: string | undefined; stableId?: string | undefined; id?: string | undefined; lat?: number | undefined; lng?: number | undefined }>,
    registerPostcodes?: readonly string[] | undefined,
): string | undefined {
    return resolveSuburbPostcode(gyms, registerPostcodes);
}

/** Base URL for canonical links (without trailing slash) */
const BASE_URL = 'https://martialartnetwork.com.au';

/** Default OG image. Points at the existing brand logo (512×512) because
 * public/og-image.jpg was never added — this fixes broken social-share previews.
 * Replace with a proper 1200×630 branded card (/og-image.jpg) when available and
 * flip this back (keep in sync with vite.config.ts DEFAULT_OG_IMAGE). */
export const DEFAULT_OG_IMAGE = `${BASE_URL}/logo-512.png`;

// SITE_NAME was removed with getDirectoryJsonLd (its only consumer). The brand
// string that actually SHIPS is vite.config.ts's ORG_NAME (SSG Organization
// JSON-LD + og:site_name) and index.html's Organization block; lib/titleCase.ts's
// stripBrandSuffix owns the title-suffix form.

// ============================================
// TYPES
// ============================================

export interface GymUrlCheck {
    correctUrl: string;
    needsRedirect: boolean;
}

export interface PageMetadata {
    title: string;
    description: string;
    canonical: string;
    robots: RobotsDirective;
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
    ogUrl: string;
    ogType: 'website' | 'place' | 'article';
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if URL has secondary filter query params.
 *
 * Only secondary filter params (age, exp, sort, styles-as-query, etc.) trigger noindex.
 * Tracking params like utm_* are ignored - they don't affect content.
 */
function hasSecondaryFilters(search: string): boolean {
    if (!search) return false;

    const params = new URLSearchParams(search);

    for (const key of params.keys()) {
        const lowerKey = key.toLowerCase();

        // Skip tracking/analytics params - they don't affect content
        if (lowerKey.startsWith('utm_') ||
            lowerKey.startsWith('fbclid') ||
            lowerKey.startsWith('gclid') ||
            lowerKey.startsWith('ref')) {
            continue;
        }

        if (SECONDARY_FILTER_PARAMS.has(lowerKey)) {
            return true;
        }
    }

    return false;
}

/**
 * Get the current full URL for self-referencing canonical.
 */
function getFullUrl(pathname: string): string {
    // Root canonical is the bare origin (no trailing slash) so it matches the shell's
    // og:url + <link rel=canonical> ('https://…au', index.html) — otherwise the homepage
    // canonical ('…au/') and og:url ('…au') would be two forms of the same URL.
    if (pathname === '/') return BASE_URL;
    return `${BASE_URL}${pathname}`;
}

/**
 * Format location slug for display in titles and headings.
 * Uses toDisplaySuburb() so clean slugs (no postcode) become title-cased names.
 * Example: "surry-hills" → "Surry Hills"
 */
function formatLocation(locationSlug: string): string {
    // ⚠️ L10 (operator-ruled 2026-08-20) — NORMALISE THROUGH THE SLUG FIRST.
    //
    // THE BUG: callers do not all pass a slug. The build passes "blacktown-2148", but
    // the runtime directory passes `filters.suburb` — the DISPLAY string the filter
    // modal stored, e.g. "Blacktown NSW 2148". `toDisplaySuburb` alone leaves that
    // intact, and the title composer below then appends state and postcode AGAIN,
    // producing the live defect "…in Blacktown NSW 2148 NSW 2148 – Martial Arts
    // Network" in the browser tab, bookmarks and shared links.
    //
    // ⭐ `toLocationSlug` is the normaliser every call shape already agrees on — it
    // strips an embedded state token and a trailing postcode from a display string and
    // is a no-op on a bare slug. Routing through it makes this function total over
    // every shape a caller actually holds, so the duplication cannot be reintroduced
    // by a new call site.
    //
    // ⚠️ VERIFIED NON-BREAKING FOR THE PRERENDERED CORPUS BEFORE SHIPPING: all 988
    // built suburbs round-trip bare-slug → display → bare-slug unchanged, so every
    // baked title is byte-identical. The blast radius here is ~4,200 pages; that check
    // is why this is a safe edit rather than a hopeful one.
    return toDisplaySuburb(toLocationSlug(locationSlug));
}

/**
 * Format a styles array for inclusion in meta descriptions.
 * ALWAYS alphabetical, at every length — the input order is never preserved.
 *   0 → ''
 *   1 → 'Boxing'
 *   2 → 'Boxing and Karate'
 *   3 → 'BJJ, Boxing and Karate'          (input ['Boxing','BJJ','Karate'])
 *  4+ → top 3 alphabetically + 'and more' → 'BJJ, Boxing, Karate and more'
 *
 * Used by getGymProfileMetadata (gym profile descriptions) and by SSG
 * (vite.config.ts) for location-page descriptions.
 */
export function formatStylesList(styles: string[]): string {
    if (styles.length === 0) return '';
    // Sort FIRST, for every length. Previously only the 4+ branch sorted, so a gym
    // with 1-3 styles listed them in raw Firestore order while suburb pages (which
    // pre-sort their own list before calling this — vite.config.ts locationStyles)
    // were alphabetical: the same data, two orders. Sorting here makes one rule for
    // every caller and matches what this function's callers already document it as
    // doing. Suburb pages are unaffected — re-sorting a sorted list is a no-op.
    const sorted = [...styles].sort((a, b) => a.localeCompare(b));
    if (sorted.length === 1) return sorted[0]!; // safe: length === 1 guarantees index 0
    if (sorted.length === 2) return `${sorted[0]} and ${sorted[1]}`;
    if (sorted.length === 3) return `${sorted[0]}, ${sorted[1]} and ${sorted[2]}`;
    // 4+ styles: top 3 alphabetically, then "and more"
    return `${sorted[0]}, ${sorted[1]}, ${sorted[2]} and more`;
}

/**
 * Extract the primary style from a URL pathname.
 * Looks for the segment after /gyms/ in /nsw/:suburb/gyms/:styleSlug paths.
 * Returns the canonical style name or empty string.
 */
function extractStyleFromPath(pathname: string): string {
    const parts = pathname.split('/');
    const gymsIdx = parts.indexOf('gyms');
    if (gymsIdx > 0 && gymsIdx + 1 < parts.length && parts[gymsIdx + 1]) {
        return parseStyleSlug(parts[gymsIdx + 1]!) || ''; // safe: guarded by the truthy check above
    }
    return '';
}

// ============================================
// STATIC PAGE METADATA
// ============================================

/**
 * Prerendered routes that must NEVER be indexed.
 *
 * These are the app's private/client-only pages. They are prerendered only so their
 * shell matches what the client renders — see the STATIC_PAGE_META entries below for
 * the full rationale (React #418 on every visit via the notfound.html fallback).
 *
 * vite.config.ts imports this to inject `noindex,follow` into each one at build time.
 * That injection is load-bearing, not belt-and-braces: nothing else in the build marks
 * these pages, so without it prerendering silently flips them from explicit noindex to
 * implicit index,follow. Kept in step with public/robots.txt by
 * lib/privateRoutePrerender.test.ts.
 */
export const PRIVATE_PRERENDERED_ROUTES = new Set<string>([
    '/auth',
    '/account',
    '/favourites',
    '/account/saved-searches',
]);

const STATIC_PAGE_META: Record<string, { title: string; description: string; canonicalPath: string }> = {
    '/': {
        title: 'Martial Arts Network',
        // Facts, not slogan (2026-08-01). The former copy was pure brand voice and Google
        // REPLACED it with a snippet of its own, stitched from one sentence of the meta plus
        // scraped page text — the observed result was "…across NSW. postcode to see nearby
        // gyms. Bondi Randwick Coogee Rose Bay Double Bay", pulling from the search helper
        // line and the suburb chips. Google substitutes when it judges a description
        // unhelpful for the query, and a slogan with no concrete detail invites exactly that.
        //
        // Two-state rewrite (2026-08-04, operator-ruled wording). The gym figure is
        // deliberately a ROUNDED FLOOR (a "N,N00+" band) so it stays true as gyms come
        // and go — stated as a CONVENTION, not a value: this comment once quoted the
        // band and the live count, and both went stale while the string below moved
        // (corrected 2026-08-31; a comment quoting a number it does not control drifts
        // by construction). The band itself tracks the corpus and is the NUMBER half of
        // the ownership marker's rule; homepageMeta.test.ts pins it against
        // data/hub-locations-all.json. The old per-state suburb count was deliberately
        // DROPPED — it is exactly what went stale, un-flagged, at the VIC launch.
        // lib/homepageMeta.test.ts now requires every LAUNCHED state to be NAMED here and
        // pins the gym-count claim against data/hub-locations-all.json, so the next state
        // launch fails the suite until this copy is re-authored.
        //
        // MUST STAY IN STEP WITH index.html, which repeats this string three times (meta
        // description, og:description, twitter:description) for the pre-hydration shell —
        // and which /notfound.html inherits.
        description: 'Search 2,600+ martial arts gyms across NSW and VIC. Compare BJJ, Boxing, Muay Thai, Karate, MMA and more by style, age and experience level.',
        canonicalPath: '/',
    },
    '/gyms': {
        // O3: brand suffix added 2026-07-31 (52 chars, still inside the display limit).
        // Two-state rewrite (2026-08-05, operator-ruled over a state-free recommendation —
        // the caveat that /gyms search still defaults to NSW and the map opens on Sydney
        // was stated and accepted; those behaviours are tracked as the item-10 handoff and
        // the state-less-search successor item in the remediation plan).
        // ⭐ 2026-08-16 (operator-ruled): "in NSW and VIC" → "across Australia". This string
        // is the SINGLE SOURCE for BOTH the SERP/tab title and the page's VISIBLE heading —
        // StateChooserPage derives its h1 by stripping the ' – Martial Arts Network' suffix
        // (ruled 2026-08-14). The operator was shown that changing the visible words
        // therefore changes the Google result too, and ruled to change BOTH rather than
        // split them into two independently-drifting strings.
        // ⚠️ The page genuinely is the country-wide entry point — it is the chooser, not a
        // claim of national coverage. The per-state titles below still say NSW and VIC, so
        // nothing here asserts gyms in states that have not launched.
        title: 'Find Martial Arts Gyms across Australia – Martial Arts Network',
        // Differentiated from the homepage 2026-08-01. Both pages previously opened
        // "…1,500+ martial arts gyms across NSW", so they read as interchangeable when
        // both surfaced for one search. This one now leads on what ONLY this page has —
        // the map — and names the tools beneath it. Every claim was checked against the
        // live page: the map, filters, sorting and saved searches all exist.
        //
        // The gym COUNT deliberately moved to the homepage alone; repeating it here spent
        // characters on a fact the visitor already has and added nothing this page does.
        //
        // UI-57 flip (operator-ruled 2026-08-14, ruling ix): /gyms is now the STATE
        // CHOOSER — the map/search moved to the per-state directories below, which took
        // this entry's former description verbatim (narrowed per state). This sentence
        // is the operator's chosen wording for the chooser, recorded verbatim; the old
        // sentence here would have been FALSE (no map, no search on this page) and a
        // duplicate of the state pages'.
        description: 'Martial arts gyms across NSW and VIC. Choose your state to search by suburb, style, age group and experience level.',
        canonicalPath: '/gyms',
    },
    '/nsw/gyms': {
        // UI-57 flip (operator-ruled 2026-08-13/14): the STATE DIRECTORY — the search
        // page that took the hub's old address. Title/description are the state-less
        // /gyms strings NARROWED PER STATE (ruling vii — pure derivation, no invention).
        // The suburb A–Z that lived at this address moved, words unchanged, to
        // '/nsw/suburbs' below (ruling viii).
        title: 'Find Martial Arts Gyms in NSW – Martial Arts Network',
        description: 'Search martial arts gyms across NSW on one map. Filter by suburb, style, age group and experience level, sort results and save your search.',
        canonicalPath: '/nsw/gyms',
    },
    '/vic/gyms': {
        // Mirrors '/nsw/gyms' above with one substitution — the state token — so the
        // two state directories read as one voice (the hub-pair convention).
        title: 'Find Martial Arts Gyms in VIC – Martial Arts Network',
        description: 'Search martial arts gyms across VIC on one map. Filter by suburb, style, age group and experience level, sort results and save your search.',
        canonicalPath: '/vic/gyms',
    },
    '/nsw/suburbs': {
        // O3: brand suffix added 2026-07-31. At 64 chars this one IS over the ~60-char
        // display limit — accepted with the same owner decision that covers the 176
        // directory/style titles below.
        title: 'Browse Martial Arts Gyms in NSW by Suburb – Martial Arts Network',
        // The suburb count is hand-written and MUST match the hub's actual link count.
        // 190 → 601 on 2026-08-01 (audit O8): the A–Z now lists EVERY suburb with a gym,
        // not just those clearing the 3-gym indexable bar, so the source of truth for this
        // number moved from data/hub-locations.json to data/hub-locations-all.json.
        // It said 189 while the page listed 188.
        // lib/seo.hubCount.test.ts fails if they diverge again — which the next
        // route-data regeneration will very likely cause.
        // 188 → 189 on 2026-07-28: the suburb-naming slice (audit H1/M1) put St Marys
        // into the hub list. It always had 5 gyms and a real page; its recorded name
        // ("Saint Marys") just never matched the register, so it was pruned from the
        // A–Z. Hand-written by owner choice; lib/seo.hubCount.test.ts fails if this
        // number ever drifts from data/hub-locations.json again.
        // M4 house voice, applied to the hub 2026-08-01. The former wording predated the
        // description rewrite and said "across NSW by suburb" and "across 601 NSW suburbs"
        // in consecutive sentences. The count stays hand-written and lib/seo.hubCount.test.ts
        // still fails if it drifts from data/hub-locations-all.json.
        // 601 → 600 on 2026-08-11: Fountaindale's only listing was a supplier, ruled not a gym
        // and removed, so the suburb has no gyms and no page. The NUMBER is a fact that must
        // track the data (lib/seo.hubCount.test.ts asserts stated == listed); the SENTENCE
        // remains the owner's hand-written copy and is untouched.
        // ⚠️⚠️ COPY OWNERSHIP — UI/UX LANE, by the operator's 2026-08-20 ruling. Read this
        // BEFORE editing the line below, because the boundary is not where it looks.
        //   • The NUMBER is DATA. `lib/seo.hubCount.test.ts` pins it to
        //     `data/hub-locations-all.json`, so a corpus change makes this line RED and
        //     correcting the digits is the right move for EITHER lane — do it, and say so.
        //   • The WORDING is COPY, and it comes to the UI/UX lane FIRST.
        // ⭐ WHY THIS COMMENT EXISTS AT ALL, since the ruling already did: the Data &
        // Pipeline lane changed these strings twice in three days, self-reported BOTH times
        // unprompted, and diagnosed it better than anyone else could have — the undertaking
        // "the next copy change comes to you first" lived only in a channel message and a
        // journal. **It was a sentence, not a mechanism.** Under a red suite the pull is to
        // fix the number and move on, and nothing in the file said to stop. Now it does.
        // Written by UI/UX v16 on 2026-08-29 at D&P's own proposal — they declined to write
        // it themselves, correctly, because editing this lane's copy to say it is this
        // lane's would have repeated the very error they were reporting.
        description: 'Find a martial arts gym in any of 598 NSW suburbs. Compare styles, age groups and experience levels.',
        canonicalPath: '/nsw/suburbs',
    },
    '/vic/suburbs': {
        // Mirrors the '/nsw/suburbs' entry above with exactly two substitutions — the
        // state token and the suburb count — so the two hubs read as one voice.
        // 64 chars, the same accepted over-length as NSW's (same owner decision).
        // UI-57 flip: this entry (formerly keyed '/vic/gyms') moved here with EVERY WORD
        // unchanged (ruling viii) — only the key and canonicalPath are new.
        title: 'Browse Martial Arts Gyms in VIC by Suburb – Martial Arts Network',
        // Hand-written, exactly as the NSW entry above is hand-written by owner choice.
        // ⚠️ This line used to name NSW's figure; it had already moved twice.
        // The count equals `data/hub-locations-all.json["vic"].length` — the count the A–Z
        // lists. ⚠️ Stated as a POINTER, not a value: this comment read "388 = …" while the
        // data said 381, because a comment quoting a number it does not control goes stale
        // the first time the number moves.
        // GUARDED (the earlier warning here that this number was unguarded is obsolete):
        // the state-generic block in lib/seo.hubCount.test.ts iterates LAUNCHED_STATES
        // and pins each state's count to data/hub-locations-all.json — this number
        // drifting fails the suite exactly the way NSW's would.
        // ⚠️⚠️ COPY OWNERSHIP — UI/UX LANE, by the operator's 2026-08-20 ruling. Read this
        // BEFORE editing the line below, because the boundary is not where it looks.
        //   • The NUMBER is DATA. `lib/seo.hubCount.test.ts` pins it to
        //     `data/hub-locations-all.json`, so a corpus change makes this line RED and
        //     correcting the digits is the right move for EITHER lane — do it, and say so.
        //   • The WORDING is COPY, and it comes to the UI/UX lane FIRST.
        // ⭐ WHY THIS COMMENT EXISTS AT ALL, since the ruling already did: the Data &
        // Pipeline lane changed these strings twice in three days, self-reported BOTH times
        // unprompted, and diagnosed it better than anyone else could have — the undertaking
        // "the next copy change comes to you first" lived only in a channel message and a
        // journal. **It was a sentence, not a mechanism.** Under a red suite the pull is to
        // fix the number and move on, and nothing in the file said to stop. Now it does.
        // Written by UI/UX v16 on 2026-08-29 at D&P's own proposal — they declined to write
        // it themselves, correctly, because editing this lane's copy to say it is this
        // lane's would have repeated the very error they were reporting.
        description: 'Find a martial arts gym in any of 382 VIC suburbs. Compare styles, age groups and experience levels.',
        canonicalPath: '/vic/suburbs',
    },
    '/about': {
        // 'About Us' matches the page's own <h1>, its footer link and the
        // SiteNavigationElement name — one name per page (URL stays /about).
        title: 'About Us – Martial Arts Network',
        // Two-state rewrite (2026-08-05, operator-ruled) — matches the About page body verbatim.
        description: 'Martial Arts Network is an independent platform built to help people discover and explore martial arts gyms across NSW and VIC.',
        canonicalPath: '/about',
    },
    '/contact': {
        title: 'Contact – Martial Arts Network',
        description: "Get in touch with the Martial Arts Network team, whether you're looking for a specific gym or have a question about the platform.",
        canonicalPath: '/contact',
    },
    '/privacy-policy': {
        title: 'Privacy Policy – Martial Arts Network',
        description: 'Read the Martial Arts Network privacy policy. How we collect, use and protect your personal information.',
        canonicalPath: '/privacy-policy',
    },
    '/terms-of-service': {
        title: 'Terms of Service – Martial Arts Network',
        description: 'Read the Martial Arts Network terms of service. The rules and conditions governing use of the platform.',
        canonicalPath: '/terms-of-service',
    },
    '/blog': {
        title: 'Blog – Martial Arts Network',
        // 156 chars (was 198 — the only description on the site past the ~160 Google
        // shows, so it was truncated mid-sentence). 'suburb guides' also now matches
        // the actual category label, which read 'suburb directories' here.
        description: 'Martial arts guides, from choosing your first style to finding a gym near you. Style guides, suburb guides and platform news for Australian martial artists.',
        canonicalPath: '/blog',
    },
    '/blog/style-guides': {
        title: 'Style Guides – Martial Arts Network',
        description: 'Explore martial arts style guides covering Boxing, BJJ, Karate, Kickboxing, Muay Thai and more. Written for beginners and practitioners across Australia.',
        canonicalPath: '/blog/style-guides',
    },
    '/blog/suburb-guides': {
        title: 'Suburb Guides – Martial Arts Network',
        description: 'Suburb-by-suburb guides to martial arts gyms near you. Find the right gym in your local area.',
        canonicalPath: '/blog/suburb-guides',
    },
    '/blog/beginners': {
        title: 'Beginners – Martial Arts Network',
        description: 'Not sure where to start? Browse beginner guides covering which style to choose, what to expect at your first class and how to find a gym near you.',
        canonicalPath: '/blog/beginners',
    },
    '/blog/platform-news': {
        title: 'Platform News – Martial Arts Network',
        description: "Platform news from Martial Arts Network. Milestones, state rollouts, listing growth and new features as Australia's gym directory expands.",
        canonicalPath: '/blog/platform-news',
    },

    // ─────────────────────────────────────────────────────────────────────────
    // PRIVATE / CLIENT-ONLY ROUTES — the register's first NON-PUBLIC entries.
    //
    // These exist so the four app routes can be PRERENDERED. They previously fell
    // through firebase.json's `**` catch-all to /notfound.html — which is a copy of
    // the HOMEPAGE shell — so the client rendered AuthPage/AccountPage/etc. against a
    // LandingPage DOM and threw a React #418 hydration mismatch on EVERY visit. That
    // is routine signed-in traffic, not rare stale links. Reshaping notfound.html
    // cannot fix it (vite-react-ssg always hydrates in production), so each route
    // needs its own matching shell. Added 2026-08-15.
    //
    // ⚠️ These four MUST stay noindex. Nothing in the build consumes the `robots`
    // field returned by getStaticPageMetadata — the only robots emitters are scoped
    // to /^\/blog/ and to thin location routes — so a META entry alone would ship
    // them as implicit index,follow, replacing today's explicit noindex and
    // contradicting public/robots.txt, which Disallows /account, /auth and
    // /favourites. The noindex is injected in vite.config.ts against
    // PRIVATE_PRERENDERED_ROUTES; the two lists are pinned together by
    // lib/privateRoutePrerender.test.ts.
    //
    // ⚠️ Titles must match each page's <DocumentTitle> byte-for-byte (note the
    // EN-DASH) or the browser tab visibly flips after hydration.
    //
    // Descriptions authored 2026-08-15 (operator-directed). House voice per the entries
    // above — concrete and second person, naming what you can actually do, no slogan.
    // These are noindex so they never surface in search results; they exist for the
    // required shape and for anyone reading the HTML.
    '/auth': {
        title: 'Sign In – Martial Arts Network',            // pages/AuthPage.tsx:178
        description: 'Sign in to Martial Arts Network to save gyms to your favourites, keep your searches and manage your account.',
        canonicalPath: '/auth',
    },
    '/account': {
        title: 'Account – Martial Arts Network',            // pages/AccountPage.tsx:320
        description: 'Manage your Martial Arts Network account. Update your details, review your saved gyms and searches, or close your account.',
        canonicalPath: '/account',
    },
    '/favourites': {
        title: 'Favourites – Martial Arts Network',         // pages/FavouritesPage.tsx:347
        description: 'Every martial arts gym you have saved, in one place. Open any listing for its styles, class times and contact details.',
        canonicalPath: '/favourites',
    },
    // NOTE the path: there is NO '/saved-searches' route. App.tsx:440 defines
    // '/account/saved-searches'; '/saved-searches' only ever 200'd because the
    // catch-all swallowed it. Prerendering that bare path would bake NotFoundPage
    // into a live-looking 200 URL — a self-inflicted soft-404.
    '/account/saved-searches': {
        title: 'Saved Searches – Martial Arts Network',     // pages/SavedSearchesPage.tsx:156
        description: 'The gym searches you have saved. Re-run one to see current listings for that suburb, style, age group and experience level.',
        canonicalPath: '/account/saved-searches',
    },
};

/**
 * Get metadata for a known static route. Returns null for unknown routes.
 */
export function getStaticPageMetadata(route: string): PageMetadata | null {
    const entry = STATIC_PAGE_META[route];
    if (!entry) return null;
    const canonical = getFullUrl(entry.canonicalPath);
    return {
        title: entry.title,
        description: entry.description,
        canonical,
        robots: 'index,follow',
        ogTitle: entry.title,
        ogDescription: entry.description,
        ogImage: DEFAULT_OG_IMAGE,
        ogUrl: canonical,
        ogType: 'website',
    };
}

// ============================================
// GYM URL REDIRECT LOGIC
// ============================================

/**
 * Get the correct gym URL based on current gym data.
 * Used to detect when gym name/location has changed and redirect is needed.
 *
 * `state` IS REQUIRED, and deliberately so. This function's result drives a real
 * `navigate(correctUrl, { replace: true })` in GymProfilePage, so a state it merely
 * DEFAULTED would not produce a cosmetically wrong link — it would bounce the visitor
 * off the page they asked for. While one state is launched the defaulted and the
 * derived value are the same string, which is exactly why the omission was invisible:
 * every live path begins with the default state, so `needsRedirect` was always false.
 * The moment a second state is launched the two diverge on every one of that state's
 * gym pages at once.
 *
 * `gymPostcode` is `string | undefined` rather than optional for the same reason: a
 * required parameter cannot follow an optional one, and making `state` optional would
 * reinstate the defect. An unmigrated 5-argument caller is now an arity error rather
 * than a silently wrong URL — the type checker, not review, enforces the migration.
 */
export function getCorrectGymUrl(
    currentPathname: string,
    gymName: string,
    gymId: string,
    gymSuburb: string,
    gymPostcode: string | undefined,
    state: StateSlug
): GymUrlCheck {
    const correctUrl = buildGymUrl(gymName, gymId, gymSuburb, gymPostcode, state);

    const normalizedCurrent = currentPathname.replace(/\/$/, '');
    const normalizedCorrect = correctUrl.replace(/\/$/, '');

    const needsRedirect = normalizedCurrent !== normalizedCorrect;

    return {
        correctUrl,
        needsRedirect
    };
}

// ============================================
// DIRECTORY METADATA
// ============================================

export interface DirectoryMetadataOptions {
    /** Number of gyms matching the canonical (suburb / suburb+style) route. SSG-only. */
    gymCount?: number;
    /** Pre-formatted styles list, e.g. "Aikido, BJJ, Boxing and more". SSG-only. Used in location-only template. */
    locationStylesList?: string;
    /**
     * The suburb's 4-digit postcode. When supplied, the TITLE switches to the
     * Australian address order the owner specified (2026-07-31, audit O2):
     *
     *     with    → "Martial Arts Gyms in Adamstown NSW 2289"
     *     without → "Martial Arts Gyms in Adamstown, NSW"      (unchanged)
     *
     * The comma is dropped only in the with-postcode form, because "Adamstown, NSW 2289"
     * is not how an Australian address is written. Omitting this is a safe no-op that
     * yields today's exact string, which is what makes the runtime fallback harmless:
     * the SSG (the copy crawlers read) always has the postcode, while a client-side
     * navigation may not, and a tab title briefly lacking a postcode is not a defect.
     *
     * Deliberately NOT applied to gym-profile titles — owner deferred those to their
     * own slice, so getGymProfileMetadata is untouched.
     */
    postcode?: string | undefined;
}

/**
 * Generate full metadata for DirectoryPage.
 *
 * /nsw/[suburb]/gyms:
 *   Title: Martial Arts Gyms in [Suburb], NSW
 *
 * /nsw/[suburb]/gyms/[style-slug]:
 *   Title: [Style] Gyms in [Suburb], NSW
 *
 * Style is extracted from the URL path (canonical) with fallback to ?styles= query param.
 *
 * `opts` is used by the SSG path to enrich descriptions with a gym count and
 * styles list. Runtime callers omit it and get a no-count fallback description.
 */
export function getDirectoryMetadata(
    pathname: string,
    search: string,
    // `| undefined` rather than `?`: TS forbids a required param (state, below)
    // after an optional one, and state being required is the point (UI-16).
    locationSlug: string | undefined,
    opts: DirectoryMetadataOptions = {},
    // UI-16: REQUIRED, not defaulted. The silent NSW default is the exact
    // mechanism that shipped wrong-state h1s on 608 VIC pages (UI-02): the
    // runtime caller omitted the argument and nothing complained. Every caller
    // passes it now; a future caller that forgets fails compilation instead.
    state: StateSlug
): PageMetadata {
    const hasFilters = hasSecondaryFilters(search);
    const stateDisplay = stateSlugToDisplay(state);
    const location = locationSlug ? formatLocation(locationSlug) : '';

    // Extract primary style: path segment takes precedence over query param
    const pathStyle = extractStyleFromPath(pathname);
    const params = new URLSearchParams(search);
    const queryStyle = params.get('styles') ? (params.get('styles') as string).split(',')[0]!.trim() : ''; // safe: split always returns ≥1 element
    const primaryStyle = pathStyle || queryStyle;

    // ── UI-73 · BACKLOG item 48's SEO half — the state-level style pages. ────────
    //
    // Handled BEFORE the location branches because these routes carry NO locationSlug:
    // without this they fall all the way through to the state-less `/gyms` chooser entry
    // and inherit its title AND its canonical. Measured before this branch existed:
    // `/nsw/gyms/bjj` returned title "Find Martial Arts Gyms in NSW and VIC" with
    // canonical `/gyms` — so 30 distinct pages would have pointed every one of them at
    // the chooser, which is the one outcome that makes the whole item pointless.
    //
    // ⭐ THE COPY IS DERIVED, NOT INVENTED, and that is the operator's ruling (2026-08-16:
    // "mirror the existing style pages", no intro paragraph). The two templates below are
    // the SAME strings the suburb style pages use at :731-734, with the state display in
    // the location slot — the same "pure derivation, no invention" the UI-57 flip applied
    // to the state directories at :396. Content & SEO is closed and all copy routes to the
    // operator, so a new sentence here would be this lane inventing copy it may not write.
    //
    // ⚠️ An UNKNOWN slug falls through deliberately. `parseStyleSlug` returns null for
    // anything outside the frozen vocabulary, and a page we cannot name must not be given
    // a confident title — it drops to the existing behaviour instead.
    // ⚰️ TOMBSTONE — UI-73's state-level style metadata arm resolved here and is GONE
    // (UI-76, 2026-08-16). It gave `/{state}/gyms/{style}` its own title, description and
    // canonical, derived from the suburb style templates below with the state in the
    // location slot. ⚠️ Removed by OPERATOR RULING on a premise reversal, not because it
    // was wrong: those urls were never intended to be pages. Item 48's SEO half is
    // WON'T-DO. Without this arm they fall through to the state-less `/gyms` chooser
    // entry — which is correct now, because they are no longer routes at all.
    let title: string;
    let description: string;
    let canonicalPath: string;

    // Title-only location label. With a postcode it takes the Australian address
    // order ("Adamstown NSW 2289"); without, it keeps today's "Adamstown, NSW".
    // DESCRIPTIONS deliberately keep the comma form either way — the owner is
    // reconfiguring description copy in its own slice, and 604 pages are already
    // over the length Google shows, so this must not spend 5 more characters there.
    // ONE resolution for the title and the description. Giving only the description a
    // fallback to the address map produced a page whose title said "Surry Hills, NSW"
    // while its own description said "Surry Hills NSW 2010" — same call, two forms.
    const resolvedPostcode = opts.postcode || getSuburbPostcode(toLocationSlug(location, state), state);
    const titleLocation = resolvedPostcode
        ? `${location} ${stateDisplay} ${resolvedPostcode}`
        : `${location}, ${stateDisplay}`;

    /**
     * M4 (owner templates, 2026-08-01). DESCRIPTIONS NOW CARRY THE POSTCODE — a
     * deliberate reversal of the titles slice, which excluded it because the descriptions
     * were already over length and could not spend 5 more characters. The rewritten
     * templates are 96–109 characters against a ~155 limit, so that objection has lapsed.
     *
     * Same Australian order as the title (`Suburb NSW Postcode`), NOT `Suburb Postcode
     * NSW` — the owner's first draft had the postcode before the state, and confirmed on
     * being asked that it should match the titles rather than sit beside them in a
     * different form in the same search result.
     *
     * Resolved from the address map when the caller supplies none, so a runtime
     * navigation prints the same string the prerendered page does.
     */
    const descLocation = titleLocation;

    // O3 (2026-07-31, owner decision): the brand suffix goes on directory and style pages,
    // which were the last indexable pages without it.
    //
    // RECORDED AS A KNOWN COST, because it was measured and chosen anyway: the postcode
    // that shipped into these titles hours earlier consumed the character budget this
    // suffix needs. Adding 23 characters pushes 144 of 190 directory titles and 31 of 225
    // style titles past the ~60 characters Google displays, so those will be truncated in
    // results. The owner was shown those numbers and chose full brand consistency over the
    // title budget. Do NOT "fix" this later by silently dropping the suffix again — that
    // would reverse an explicit decision; reopen it with the owner instead.
    if (location) {
        if (primaryStyle) {
            title = `${primaryStyle} Gyms in ${titleLocation} – Martial Arts Network`;
            description = `Find a ${primaryStyle} martial arts gym in ${descLocation}. Compare age groups and experience levels.`;
            canonicalPath = buildStyleUrl(location, primaryStyle, state);
        } else {
            title = `Martial Arts Gyms in ${titleLocation} – Martial Arts Network`;
            description = `Find a martial arts gym in ${descLocation}. Compare styles, age groups and experience levels.`;
            canonicalPath = buildDirectoryUrl(location, state);
        }
    } else {
        // No location. TWO identities live in this arm since the UI-57 flip, resolved
        // by the page's own address so head, baked sr-only <h1> and hydrated tab title
        // all agree (review-caught FATAL, 2026-08-14: this arm used to hand the state
        // roots the CHOOSER's identity — head said "…in NSW", body said "…in NSW and
        // VIC", the UI-02 contradiction class):
        //  · a STATE DIRECTORY ROOT ('/{state}/gyms') → its own STATIC_PAGE_META entry
        //    (ruling vii), via the armed stateDirectoryRootSlug matcher;
        //  · anything genuinely state-less → the '/gyms' chooser entry (byte-equality
        //    with SSG output for that page, as before).
        // Robots reflects whether secondary filters are present (overrides index,follow).
        const rootState = stateDirectoryRootSlug(pathname);
        const staticEntry = getStaticPageMetadata(rootState !== null ? buildStateDirectoryRootUrl(rootState) : '/gyms');
        if (staticEntry) {
            return { ...staticEntry, robots: hasFilters ? 'noindex,follow' : 'index,follow' };
        }
        // Defensive fallback (should never hit — both keys above are always in STATIC_PAGE_META)
        title = 'Find Martial Arts Gyms in NSW and VIC';
        description = 'Browse martial arts gyms across NSW and VIC. Compare classes for beginners, kids and adults. Find the right gym and start training.';
        canonicalPath = '/gyms';
    }

    // Canonical is ALWAYS the clean path (no query params)
    const canonical = getFullUrl(canonicalPath);
    // Robots: secondary filters → noindex,follow (non-canonical). Otherwise a
    // clean suburb/style page is indexed ONLY if it meets the ≥MIN_GYMS_FOR_INDEX
    // bar; thinner pages are noindex,follow (link equity still flows). The count
    // is SSG-only (opts.gymCount); runtime callers omit it and default to indexed
    // — the load-bearing crawler-facing value is injected at SSG build time.
    const isThin = location !== '' && typeof opts.gymCount === 'number' && opts.gymCount < MIN_GYMS_FOR_INDEX;
    const robots = (hasFilters || isThin) ? 'noindex,follow' : 'index,follow';

    return {
        title,
        description,
        canonical,
        robots,
        ogTitle: title,
        ogDescription: description,
        ogImage: DEFAULT_OG_IMAGE,
        ogUrl: canonical,
        ogType: 'website'
    };
}

// ============================================
// GYM PROFILE METADATA
// ============================================

/**
 * Generate full metadata for GymProfilePage.
 *
 * Title: [Gym Name] – Martial Arts Network
 * Canonical: /gyms/[suburb-slug]/[gym-slug]-g[id]
 */
export function getGymProfileMetadata(
    gymName: string,
    gymId: string,
    suburb: string,
    // `| undefined` rather than `?` on the three below: TS forbids a required
    // param (state) after an optional one, and state being required is the
    // point (UI-16). Callers must still fill every slot.
    postcode: string | undefined,
    // M4: the description no longer lists the styles, so this is unread — but it stays in
    // the signature because `imageUrl` and `state` follow it positionally and every caller
    // passes it. Removing it would silently shift those two arguments.
    _allStyles: string[] | undefined,
    imageUrl: string | undefined,
    // UI-16: REQUIRED, not defaulted — see getDirectoryMetadata's note. This is
    // the parameter whose omission rewrote 1,875 VIC tab titles to NSW (UI-02).
    state: StateSlug
): PageMetadata {
    const correctUrl = buildGymUrl(gymName, gymId, suburb, postcode, state);
    const canonical = getFullUrl(correctUrl);
    const stateDisplay = stateSlugToDisplay(state);

    // NOTE: en-dash U+2013 between gym name and suburb. Do not substitute U+002D hyphen-minus.
    //
    // O2 (2026-07-31): the postcode joins gym titles, in the same Australian order and
    // comma-less form the owner chose for suburb pages — "Adamstown NSW 2289".
    //
    // THE POSTCODE IS THE SUBURB'S, NOT THE GYM RECORD'S, and that is deliberate. The
    // `postcode` argument carries the business's own recorded value, which disagrees with
    // the locality register on 61 of 1,496 gyms — and every disagreement spot-checked in
    // the titles slice found the REGISTER right and the record wrong (Miranda really is
    // 2228). Printing the record would put a wrong postcode on those 61 pages AND make the
    // title contradict the page's own URL, which is addressed from the register. Falls back
    // to the record only where the register has no entry, and to the comma form when
    // neither resolves. (The gym's own postcode is still published, correctly, as the
    // JSON-LD postalCode — that field describes the business's address, this one labels
    // the suburb. F3 is the open item that reconciles the two.)
    const titlePc = getSuburbPostcode(toLocationSlug(suburb, state), state)
        || (/^\d{4}$/.test(String(postcode ?? '').trim()) ? String(postcode).trim() : '');
    const title = titlePc
        ? `${gymName} – ${suburb} ${stateDisplay} ${titlePc} – Martial Arts Network`
        : `${gymName} – ${suburb}, ${stateDisplay} – Martial Arts Network`;
    /**
     * M4 owner template. `stylesList` is no longer interpolated — the sentence names
     * "styles" as something the page shows rather than listing them, which keeps the
     * length independent of how many styles a gym teaches. "Reviews" was dropped on
     * evidence: a live gym page was checked and renders styles, age groups, location and
     * contact details, but NOT a reviews section, so advertising it would have promised
     * something absent.
     */
    const description = titlePc
        ? `${gymName}, a martial arts gym in ${suburb} ${stateDisplay} ${titlePc}. See styles, age groups, location, contact details and more.`
        : `${gymName}, a martial arts gym in ${suburb}, ${stateDisplay}. See styles, age groups, location, contact details and more.`;

    return {
        title,
        description,
        canonical,
        robots: 'index,follow',
        ogTitle: title,
        ogDescription: description,
        ogImage: imageUrl || DEFAULT_OG_IMAGE,
        ogUrl: canonical,
        ogType: 'place'
    };
}

// ============================================
// STRUCTURED DATA (JSON-LD)
// ============================================

/**
 * Input data for gym structured data generation.
 */
export interface GymStructuredDataInput {
    name: string;
    id: string;
    stableId?: string | undefined;
    addressLine1: string;
    suburb: string;
    /** Display form, e.g. 'NSW'. Absent (pre-R3 display docs) resolves to the default state. */
    state?: string | undefined;
    postcode: string;
    lat?: number;
    lng?: number;
    phoneNumber?: string | undefined;
    websiteUrl?: string | undefined;
    openingHours?: string[];
    googleMapsUrl?: string | undefined;
    google_rating?: number | undefined;
    google_user_ratings_total?: number | undefined;
    allStyles?: string[];
}

/**
 * Generate JSON-LD structured data for GymProfilePage.
 * Uses LocalBusiness and SportsActivityLocation schema types.
 */
export function getGymJsonLd(gym: GymStructuredDataInput): object {
    // Absent state field (pre-R3 display docs) resolves to the default state
    // (R2 locked decision 5) — output byte-identical to the former 'NSW' literal.
    const stateDisplay = gym.state || DEFAULT_STATE_DISPLAY;
    const stateSlug = stateDisplayToSlug(stateDisplay) as StateSlug;
    const canonicalUrl = getFullUrl(buildGymUrl(gym.name, gym.stableId || gym.id, gym.suburb, gym.postcode, stateSlug));

    const jsonLd: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': ['LocalBusiness', 'SportsActivityLocation'],
        '@id': canonicalUrl,
        name: gym.name,
        url: canonicalUrl,
        address: {
            '@type': 'PostalAddress',
            streetAddress: gym.addressLine1,
            addressLocality: gym.suburb,
            addressRegion: stateDisplay,
            postalCode: gym.postcode,
            addressCountry: 'AU'
        }
    };

    // Add geo coordinates if available and valid
    if (typeof gym.lat === 'number' &&
        typeof gym.lng === 'number' &&
        !isNaN(gym.lat) &&
        !isNaN(gym.lng)) {
        jsonLd.geo = {
            '@type': 'GeoCoordinates',
            latitude: gym.lat,
            longitude: gym.lng
        };
    }

    // Add telephone if available and not empty
    if (gym.phoneNumber && gym.phoneNumber.trim().length > 0) {
        jsonLd.telephone = gym.phoneNumber.trim();
    }

    // Add website if available
    if (gym.websiteUrl) {
        jsonLd.sameAs = [gym.websiteUrl];
    }

    // Add Google Maps URL
    if (gym.googleMapsUrl) {
        if (Array.isArray(jsonLd.sameAs)) {
            (jsonLd.sameAs as string[]).push(gym.googleMapsUrl);
        } else {
            jsonLd.sameAs = [gym.googleMapsUrl];
        }
    }

    // Add opening hours if available.
    //
    // gym.openingHours holds Google Places' human display text ("Monday: 6:30 –
    // 9:30 PM"). That is NOT schema.org's `openingHours` grammar, and assigning
    // it straight through published an unreadable value on every gym page that
    // had hours. toOpeningHoursSpecification converts it to the object form
    // Google documents, dropping anything it cannot convert with certainty.
    // Do NOT reinstate a raw `jsonLd.openingHours = gym.openingHours` here.
    const openingHoursSpecification = toOpeningHoursSpecification(gym.openingHours);
    if (openingHoursSpecification.length > 0) {
        jsonLd.openingHoursSpecification = openingHoursSpecification;
    }

    // Add aggregate rating if reliable
    const hasValidRating = typeof gym.google_rating === 'number' &&
        !isNaN(gym.google_rating) &&
        gym.google_rating > 0 &&
        gym.google_rating <= 5;
    const hasValidReviewCount = typeof gym.google_user_ratings_total === 'number' &&
        gym.google_user_ratings_total > 0;

    if (hasValidRating && hasValidReviewCount) {
        jsonLd.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: gym.google_rating,
            reviewCount: gym.google_user_ratings_total,
            bestRating: 5,
            worstRating: 1
        };
    }

    // Add sports activity type
    if (gym.allStyles && gym.allStyles.length > 0) {
        jsonLd.sport = gym.allStyles;
    }

    return jsonLd;
}

// getDirectoryJsonLd (CollectionPage) + DirectoryStructuredDataInput were removed
// 2026-07-25: audit item #4 unified directory structured data on the SSG's
// ItemList shape (buildLocationItemListJsonLd), leaving this builder with no
// caller outside its own tests. Removed so the live ItemList builder is the only
// directory JSON-LD source and nobody re-wires the CollectionPage one by mistake.

/**
 * TS-L7 tranche 2 (2026-08-07) — the SSG's title-postcode DECISION, extracted
 * verbatim from vite.config.ts (which already imports this module). The
 * register wins over the gym records; a disagreement between them surfaces
 * through the callback (the build logs it once per slug) because that signal
 * caught a duplicated venue at hunters-hill — silence here is what let the
 * first implementation ship a wrong title on an indexed page.
 */
export function resolveTitlePostcodeFromSources(
    lookupKey: string,
    gyms: Parameters<typeof titlePostcode>[0],
    register: Map<string, string[]>,
    onDisagreement?: (d: { slug: string; register: string; gyms: string }) => void,
): string | undefined {
    const candidates = register.get(lookupKey) ?? [];
    const fromRegister = candidates.length === 1 ? candidates[0] : undefined;
    const fromGyms = titlePostcode(gyms);
    if (fromRegister && fromGyms && fromRegister !== fromGyms) {
        onDisagreement?.({ slug: lookupKey, register: fromRegister, gyms: fromGyms });
    }
    return titlePostcode(gyms, candidates);
}
